import { createContext, useContext, useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CustomEase } from "gsap/CustomEase";
import { SplitText } from "gsap/SplitText";
import Lenis from "lenis";
import { EASE_BEZIER } from "./tokens";

// ---------------------------------------------------------------------------
// MotionProvider — the single runtime for all site motion.
//  • Registers GSAP plugins + the "luxe" ease exactly once.
//  • Runs one Lenis instance (smooth scroll) synced to ScrollTrigger's clock.
//  • KILL-SWITCH: motion is fully disabled when
//      a) running under the pre-renderer (navigator.webdriver) — so the static
//         HTML snapshot Google reads NEVER contains hidden/offset content, or
//      b) the visitor prefers reduced motion (accessibility).
//    When disabled, hooks no-op and content renders in its final visible state.
// ---------------------------------------------------------------------------

const MotionContext = createContext({ enabled: false, lenis: null });
export const useMotion = () => useContext(MotionContext);

export function motionAllowed() {
    if (typeof window === "undefined") return false;
    if (navigator.webdriver) return false; // prerender / headless
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    return true;
}

export default function MotionProvider({ children }) {
    const [enabled, setEnabled] = useState(false);
    const lenisRef = useRef(null);

    useEffect(() => {
        if (!motionAllowed()) return;

        gsap.registerPlugin(ScrollTrigger, CustomEase, SplitText);
        if (!CustomEase.get("luxe")) CustomEase.create("luxe", EASE_BEZIER);
        gsap.defaults({ ease: "luxe" });

        const lenis = new Lenis({
            duration: 1.1,
            smoothWheel: true,
            touchMultiplier: 1.4,
        });
        lenisRef.current = lenis;

        // Single clock: GSAP's ticker drives Lenis; Lenis drives ScrollTrigger.
        lenis.on("scroll", ScrollTrigger.update);
        const raf = (time) => lenis.raf(time * 1000);
        gsap.ticker.add(raf);
        gsap.ticker.lagSmoothing(0);

        setEnabled(true);

        return () => {
            gsap.ticker.remove(raf);
            lenis.destroy();
            lenisRef.current = null;
            ScrollTrigger.getAll().forEach((t) => t.kill());
            setEnabled(false);
        };
    }, []);

    return (
        <MotionContext.Provider value={{ enabled, lenis: lenisRef.current }}>
            {children}
        </MotionContext.Provider>
    );
}
